import { Icon } from "@/lib/icons";
import { downloadQuestionsTemplate } from "@/utils/excel-import-templates";
import { cn } from "@/lib/utils";
import type { Language } from "@/lib/types/language";
import type { Sector } from "@/types/theme.types";
import type { PreviewData } from "./use-theme-config";
import type { translations } from '@/translations/translations';

type T = typeof translations.fr.admin.themeConfig;

interface Props {
  excelTitle: string;
  excelDescription: string;
  excelSector: string;
  excelSectorCustom: string;
  excelLanguage: string;
  excelFile: File | null;
  isExcelLoading: boolean;
  previewData: PreviewData | null;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  t: T;
  onTitleChange: (v: string) => void;
  onDescriptionChange: (v: string) => void;
  onSectorChange: (v: string) => void;
  onSectorCustomChange: (v: string) => void;
  onLanguageChange: (v: string) => void;
  onFileSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveFile: () => void;
  onOpenPreview: () => void;
  onSubmit: () => void;
  languages: Language[];
  isLanguagesLoading: boolean;
  sectors: Sector[];
  isSelectorsLoading: boolean;
}

export function ExcelUploadForm({
  excelTitle, excelDescription, excelSector, excelSectorCustom, excelLanguage, excelFile, isExcelLoading, previewData, fileInputRef,
  t, onTitleChange, onDescriptionChange, onSectorChange, onSectorCustomChange, onLanguageChange, onFileSelect, onRemoveFile, onOpenPreview, onSubmit,
  languages, isLanguagesLoading,
  sectors, isSelectorsLoading,
}: Props) {
  return (
    <section className="pt-7">

      <div className="grid gap-8 lg:grid-cols-[minmax(0,1.2fr)_minmax(280px,0.8fr)]">
        <div className="space-y-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t.excelForm.title}>
              <input value={excelTitle} onChange={(e) => onTitleChange(e.target.value)} placeholder={t.excelForm.titlePlaceholder} disabled={isExcelLoading} className={controlClass} />
            </Field>
            <div>
              <Field label={t.excelForm.sector}>
                <select
                  value={excelSector}
                  onChange={(e) => {
                    onSectorChange(e.target.value);
                    if (e.target.value !== "autre") {
                      onSectorCustomChange("");
                    }
                  }}
                  disabled={isExcelLoading || isSelectorsLoading}
                  className={controlClass}
                >
                  <option value="">{t.excelForm.sectorPlaceholder}</option>
                  {sectors.map((sector) => (
                    <option key={sector.id} value={sector.name}>
                      {sector.name.charAt(0).toUpperCase() + sector.name.slice(1)}
                    </option>
                  ))}
                  <option value="autre">Autre</option>
                </select>
              </Field>
              {excelSector === "autre" && (
                <div className="mt-2">
                  <input
                    value={excelSectorCustom}
                    onChange={(e) => onSectorCustomChange(e.target.value)}
                    placeholder="Spécifier votre secteur"
                    disabled={isExcelLoading}
                    className={controlClass}
                  />
                </div>
              )}
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t.excelForm.description}>
              <textarea value={excelDescription} onChange={(e) => onDescriptionChange(e.target.value)} placeholder={t.excelForm.descriptionPlaceholder} rows={2} disabled={isExcelLoading} className={cn(textareaClass, "resize-none")} />
            </Field>
            <Field label={t.excelForm.language}>
              <select
                value={excelLanguage}
                onChange={(e) => onLanguageChange(e.target.value)}
                disabled={isExcelLoading || isLanguagesLoading}
                className={controlClass}
              >
                <option value="">{t.excelForm.languagePlaceholder}</option>
                {languages.map((language) => (
                  <option key={language.id} value={language.code}>
                    {language.displayName || language.name}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          <button
            type="button"
            onClick={downloadQuestionsTemplate}
            className="inline-flex max-w-full items-center gap-2 rounded-lg border border-border/40 px-4 py-2 text-sm font-semibold text-foreground hover:bg-muted/30"
          >
            <Icon name="download" className="h-4 w-4 shrink-0" />
            {t.excelForm.downloadTemplate}
          </button>

          <div className="rounded-xl border-2 border-dashed border-border/40 bg-muted/10 p-6">
            {excelFile ? (
              <div className="flex items-center gap-4">
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl border border-success/[0.18] bg-success/[0.06] text-success">
                  <Icon name="fileText" className="h-5 w-5" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-foreground">{excelFile.name}</p>
                  <p className="text-xs text-muted-foreground">{(excelFile.size / 1024).toFixed(1)} KB</p>
                </div>
                <button
                  type="button"
                  onClick={onRemoveFile}
                  disabled={isExcelLoading}
                  className="inline-flex items-center gap-1.5 rounded-full border border-border/40 bg-transparent px-3 py-1.5 text-xs font-semibold text-muted-foreground hover:text-foreground"
                >
                  <Icon name="x" className="h-3.5 w-3.5" />
                  {t.excelForm.changeFile}
                </button>
              </div>
            ) : (
              <div className="text-center">
                <span className="mx-auto grid h-10 w-10 place-items-center rounded-xl border border-border/40 bg-muted/20 text-muted-foreground">
                  <Icon name="upload" className="h-5 w-5" />
                </span>
                <p className="mt-3 text-sm text-foreground/80">
                  <button type="button" onClick={() => fileInputRef.current?.click()} disabled={isExcelLoading} className="font-semibold text-brand hover:underline">
                    {t.excelForm.dragAndDrop}
                  </button>
                </p>
                <p className="mt-1 text-xs text-muted-foreground">.xlsx, .xls, .csv</p>
              </div>
            )}
            <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" onChange={onFileSelect} className="hidden" disabled={isExcelLoading} />
          </div>

          {previewData && previewData.rows.length > 0 && (
            <div className="rounded-xl border border-success/15 bg-success/[0.04] p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-success">{t.excelForm.previewTitle.replace("{count}", String(previewData.rows.length))}</p>
                  <p className="text-xs text-muted-foreground">{t.excelForm.readyToImport.replace("{count}", String(previewData.rows.length))}</p>
                </div>
                <button type="button" onClick={onOpenPreview} className="inline-flex items-center gap-1.5 rounded-full border border-border/40 bg-transparent px-3 py-1.5 text-xs font-semibold text-muted-foreground hover:text-foreground">
                  <Icon name="eye" className="h-3.5 w-3.5" />
                  {t.excelForm.viewDetails}
                </button>
              </div>
            </div>
          )}

          {previewData && previewData.errors.length > 0 && (
            <div className="rounded-xl border border-danger/15 bg-danger/[0.04] p-4">
              <p className="flex items-center gap-2 text-sm font-semibold text-danger">
                <Icon name="alertCircle" className="h-4 w-4" />
                {t.excelForm.errorsDetected}
              </p>
              <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
                {previewData.errors.slice(0, 5).map((error, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <span className="mt-1 h-1 w-1 shrink-0 rounded-full bg-danger/60" />
                    {error}
                  </li>
                ))}
                {previewData.errors.length > 5 && (
                  <li className="text-muted-foreground">{t.excelForm.andMore.replace("{count}", String(previewData.errors.length - 5))}</li>
                )}
              </ul>
            </div>
          )}
        </div>

        <div className="space-y-6">
          <div className="rounded-xl border border-border/40 bg-muted/10 p-5">
            <p className="flex items-center gap-2 text-[0.64rem] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
              <Icon name="info" className="h-3 w-3" />
              {t.excelForm.excelFormatRequired}
            </p>
            <ul className="mt-3 space-y-2 text-xs text-muted-foreground">
              <li className="flex items-start gap-2">
                <span className="mt-1 h-1 w-1 shrink-0 rounded-full bg-brand/50" />
                <span><span className="font-semibold text-foreground/80">questionFr</span> : {t.excelForm.questionFrRequired.split(" - ")[1]}</span>
              </li>
            </ul>
          </div>

          <div className="rounded-xl border border-border/40 bg-muted/10 p-5">
            <p className="flex items-center gap-2 text-[0.64rem] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
              <Icon name="fileText" className="h-3 w-3" />
              {t.excelForm.selectFile}
            </p>
            <p className="mt-3 text-xs leading-5 text-muted-foreground/[0.72]">{t.excelForm.maxSize}</p>
          </div>
        </div>
      </div>

      <div className="mt-8 flex items-center justify-end gap-3 pt-5">
        <button
          type="button"
          onClick={onSubmit}
          disabled={!excelFile || !previewData?.isValid || isExcelLoading}
          className="inline-flex items-center gap-1.5 rounded-full border border-brand/20 bg-brand px-5 py-2 text-sm font-semibold text-slate-950 transition-colors hover:bg-brand disabled:border-border/40 disabled:bg-muted/20 disabled:text-muted-foreground"
        >
          {isExcelLoading ? (
            <span className="mr-1.5 h-4 w-4 animate-spin rounded-full border-2 border-slate-950 border-t-transparent" />
          ) : (
            <Icon name="upload" className="h-3.5 w-3.5" />
          )}
          {isExcelLoading ? t.excelForm.uploading : t.excelForm.createAndImport}
        </button>
      </div>
    </section>
  );
}

const controlClass =
  "h-10 w-full rounded-xl border border-border/40 bg-muted/20 px-3.5 text-sm text-foreground shadow-none placeholder:text-muted-foreground/70 focus:outline-none focus:ring-1 focus:ring-brand/45 disabled:opacity-50";

const textareaClass =
  "w-full rounded-xl border border-border/40 bg-muted/20 px-3.5 py-3 text-sm text-foreground shadow-none placeholder:text-muted-foreground/70 focus:outline-none focus:ring-1 focus:ring-brand/45 disabled:opacity-50";

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block space-y-2">
      <span className="text-[0.64rem] font-semibold uppercase tracking-[0.22em] text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}
